import { intl } from '@cedalo/webui/src/helper/IntlGlobalProvider';
import { runQuery } from '@cedalo/webui/src/ui/app/GraphQLWSClient';
import { Overlay } from '@cedalo/webui/src/ui/utils/Overlay';
import CheckIcon from '@mui/icons-material/esm/Check';
import { Button, CircularProgress, Grid, TextField, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';

const workspaceShape = PropTypes.shape({
	name: PropTypes.string
});

const CreateWorkspaceForm = (props) => {
	const { workspace, errors, valid, disabled, pristine, onNameUpdate, onCancel, onSubmit } = props;

	const errorsMappings = {
		NAME_INVALID: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameInvalid' }, {}),
		NAME_IN_USE: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameInUse' }, {}),
		NAME_REQUIRED: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameRequired' }, {}),
		UNEXPECTED_ERROR: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorUnexpected' }, {})
	};

	const getError = (code) => (code ? errorsMappings[code] || errorsMappings.UNEXPECTED_ERROR : undefined);

	return (
		<form>
			<Grid container spacing={2}>
				<Grid item container spacing={8} justifyContent="space-between" alignItems="center">
					<Grid item />
					{errors.form ? (
						<Grid item>
							<Typography color="error" variant="subtitle1">
								{getError(errors.form)}
							</Typography>
						</Grid>
					) : undefined}
				</Grid>
				<Grid item xs={12} sm={12}>
					<TextField
						required
						variant="outlined"
						size="small"
						id="name"
						label={<FormattedMessage id="Extensions.Admin.Workspace.labelName" defaultMessage="Name" />}
						fullWidth
						error={!!errors.name}
						helperText={getError(errors.name)}
						disabled={disabled}
						value={workspace.name}
						onChange={(event) => onNameUpdate(event.target.value)}
					/>
				</Grid>
				<Grid container item spacing={2} xs={12} justifyContent="flex-end" direction="row">
					<Grid item>
						<Button variant="outlined" disabled={disabled} onClick={onCancel}>
							<FormattedMessage id="Extensions.Admin.Workspace.buttonCancel" defaultMessage="Cancel" />
						</Button>
					</Grid>
					<Grid item>
						<Button variant="outlined" onClick={onSubmit} disabled={pristine || !valid || disabled}>
							<FormattedMessage id="Extensions.Admin.Workspace.buttonAdd" defaultMessage="Add" />
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</form>
	);
};

CreateWorkspaceForm.propTypes = {
	workspace: workspaceShape.isRequired,
	errors: PropTypes.shape({
		name: PropTypes.string
	}).isRequired,
	valid: PropTypes.bool.isRequired,
	disabled: PropTypes.bool.isRequired,
	pristine: PropTypes.bool.isRequired,
	onNameUpdate: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired
};

const createWorkspaceReducer = (state, action) => {
	switch (action.type) {
		case 'set_name':
			return {
				...state,
				pristine: false,
				workspace: { ...state.workspace, name: action.data },
				errors: {
					...state.errors,
					name: !action.data ? 'NAME_REQUIRED' : undefined
				}
			};
		case 'save':
			return {
				...state,
				savePending: true
			};
		case 'saving_error':
			return {
				...state,
				savePending: false,
				pristine: true,
				errors: action.data
			};
		case 'saving_success':
			return {
				...state,
				savePending: false,
				saved: true
			};
		default:
			throw new Error('Unkown action');
	}
};

const CREATE_WORKSPACE_MUTATION = `
mutation CreateWorkspace($workspace: WorkspaceInput!) {
	createWorkspace(workspace: $workspace) {
		workspace {
			id
			name
		}
		success
		code
		fieldErrors {
			name
		}
	}
}
`;

const hasFieldError = (errors) => Object.entries(errors).filter(([key, value]) => key !== 'form' && !!value).length > 0;

export const CreateWorkspaceView = (props) => {
	const { onCancel, onSubmit } = props;
	const [state, dispatch] = useReducer(createWorkspaceReducer, {
		pristine: true,
		workspace: {
			name: ''
		},
		errors: {},
		savePending: false,
		saved: false
	});

	useEffect(() => {
		if (state.saved) {
			setTimeout(() => {
				onSubmit(state.workspace);
			}, 500);
		}
	}, [state.saved]);

	const hasError = hasFieldError(state.errors);
	const hasRequired = state.workspace.name;
	const isValid = !!(!hasError && hasRequired);

	const saveWorkspace = async () => {
		dispatch({ type: 'save' });
		try {
			const {
				createWorkspace: { success, fieldErrors, code, workspace }
			} = await runQuery(CREATE_WORKSPACE_MUTATION, {
				workspace: state.workspace
			});
			if (success) {
				dispatch({ type: 'saving_success', data: workspace });
			} else if (fieldErrors) {
				dispatch({ type: 'saving_error', data: fieldErrors });
			} else {
				dispatch({ type: 'saving_error', data: { form: code } });
			}
		} catch (error) {
			dispatch({ type: 'saving_error', data: { form: 'UNEXPECTED_ERROR' } });
			console.error(error);
		}
	};

	return (
		<div
		>
			<CreateWorkspaceForm
				workspace={state.workspace}
				errors={state.errors}
				pristine={state.pristine}
				valid={isValid}
				disabled={state.savePending || state.saved}
				onCancel={onCancel}
				onSubmit={saveWorkspace}
				onNameUpdate={(value) => dispatch({ type: 'set_name', data: value })}
			/>
			{state.savePending && (
				<Overlay>
					<CircularProgress style={{ width: '24px', height: '24px' }} />
				</Overlay>
			)}
			{state.saved && (
				<Overlay>
					<CheckIcon color="primary" />
				</Overlay>
			)}
		</div>
	);
};

CreateWorkspaceView.propTypes = {
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired
};
